// 效果處理器集合
// Effect Handlers Collection
// 統一匯出所有效果處理器

import type { GameState, Card } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('index');

// ============================================================
// 基礎效果處理器（原有）
// ============================================================

/**
 * 破壞效果處理器
 * 處理「使X被破壞」類型的效果
 */
export class DestroyEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被破壞') || text === 'destroy';
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使敵方場上X單位被破壞」
    const enemyDestroyMatch = effectText.match(/使敵方(場上)?(\d*)個?([^被]+)?被破壞/);
    if (enemyDestroyMatch) {
      actions.push({
        type: 'destroy',
        sourceCardId: '',
        targetSelector: {
          zone: 'battlefield',
          side: 'enemy',
          count: enemyDestroyMatch[2] ? parseInt(enemyDestroyMatch[2], 10) : 1,
        },
      });
    }

    // 解析「使你場上X單位被破壞」（犧牲效果）
    const selfDestroyMatch = effectText.match(/使你(場上)?(\d*)個?([^被]+)?被破壞/);
    if (selfDestroyMatch && !enemyDestroyMatch) {
      actions.push({
        type: 'destroy',
        sourceCardId: '',
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
          count: selfDestroyMatch[2] ? parseInt(selfDestroyMatch[2], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const isEnemy = selector.side === 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isEnemy ? !activeIsPlayer1 : activeIsPlayer1;
    
    const sideKey = targetIsPlayer1 ? 'player1Side' : 'player2Side';
    const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
    
    let newState = state;
    let destroyed = 0;

    const destroyFromLine = (lineKey: 'frontLine' | 'backLine'): void => {
      if (destroyed >= count) return;

      const side = newState.battlefield[sideKey];
      const line = [...side[lineKey]];

      for (let i = 0; i < line.length && destroyed < count; i++) {
        if (line[i].unit) {
          const destroyedUnit = line[i].unit;
          line[i] = { ...line[i], unit: null };
          destroyed++;

          const player = newState[playerKey];
          const newGraveyard = [...player.graveyard, { ...destroyedUnit, location: 'graveyard' as const }];

          newState = {
            ...newState,
            battlefield: {
              ...newState.battlefield,
              [sideKey]: {
                ...newState.battlefield[sideKey],
                [lineKey]: line,
              },
            },
            [playerKey]: {
              ...player,
              graveyard: newGraveyard,
            },
          };
        }
      }
    };

    destroyFromLine('frontLine');
    destroyFromLine('backLine');

    log.debug(`Destroyed ${String(destroyed)} units`);
    return newState;
  }
}

/**
 * 回手牌效果處理器
 */
export class ReturnToHandEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('回手牌') || text === 'return';
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const returnMatch = effectText.match(/使(敵方|你)(場上)?(\d*)個?([^回]+)?回手牌/);
    if (returnMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'returnToHand',
        targetSelector: {
          zone: 'battlefield',
          side: returnMatch[1] === '敵方' ? 'enemy' : 'self',
          count: returnMatch[3] ? parseInt(returnMatch[3], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'returnToHand') return state;

    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const isEnemy = selector.side === 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isEnemy ? !activeIsPlayer1 : activeIsPlayer1;
    
    const sideKey = targetIsPlayer1 ? 'player1Side' : 'player2Side';
    const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
    
    let newState = state;
    let returned = 0;

    const returnFromLine = (lineKey: 'frontLine' | 'backLine'): void => {
      if (returned >= count) return;

      const side = newState.battlefield[sideKey];
      const line = [...side[lineKey]];

      for (let i = 0; i < line.length && returned < count; i++) {
        if (line[i].unit) {
          const returnedUnit = line[i].unit;
          line[i] = { ...line[i], unit: null };
          returned++;

          const player = newState[playerKey];
          const newHand = [...player.hand, { ...returnedUnit, location: 'hand' as const, position: undefined }];

          newState = {
            ...newState,
            battlefield: {
              ...newState.battlefield,
              [sideKey]: {
                ...newState.battlefield[sideKey],
                [lineKey]: line,
              },
            },
            [playerKey]: {
              ...player,
              hand: newHand,
            },
          };
        }
      }
    };

    returnFromLine('frontLine');
    returnFromLine('backLine');

    log.debug(`Returned ${String(returned)} units to hand`);
    return newState;
  }
}

/**
 * 橫放效果處理器
 */
export class TapEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被橫放') || text === 'tap';
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const tapMatch = effectText.match(/使(敵方|你|該)(場上)?(\d*)個?([^被]+)?被橫放/);
    if (tapMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'tap',
        targetSelector: {
          zone: 'battlefield',
          side: tapMatch[1] === '敵方' ? 'enemy' : 'self',
          count: tapMatch[3] ? parseInt(tapMatch[3], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'tap') return state;

    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const isEnemy = selector.side === 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isEnemy ? !activeIsPlayer1 : activeIsPlayer1;
    
    const sideKey = targetIsPlayer1 ? 'player1Side' : 'player2Side';
    
    let newState = state;
    let tapped = 0;

    const tapInLine = (lineKey: 'frontLine' | 'backLine'): void => {
      if (tapped >= count) return;

      const side = newState.battlefield[sideKey];
      const line = [...side[lineKey]];

      for (let i = 0; i < line.length && tapped < count; i++) {
        const unit = line[i].unit;
        if (unit && unit.orientation !== 'tapped') {
          line[i] = {
            ...line[i],
            unit: { ...unit, orientation: 'tapped' },
          };
          tapped++;
        }
      }

      newState = {
        ...newState,
        battlefield: {
          ...newState.battlefield,
          [sideKey]: {
            ...newState.battlefield[sideKey],
            [lineKey]: line,
          },
        },
      };
    };

    tapInLine('frontLine');
    tapInLine('backLine');

    log.debug(`Tapped ${String(tapped)} units`);
    return newState;
  }
}

/**
 * 小旅人召喚效果處理器
 */
export class SummonTravelerEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('小旅人') && (text.includes('召喚') || text === 'summonTraveler');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const travelerMatch = effectText.match(/召喚(\d+)個小旅人/);
    if (travelerMatch) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        targetSelector: {
          zone: 'extra',
          side: 'self',
          filter: { name: '小旅人' },
          count: parseInt(travelerMatch[1], 10),
        },
      });
    }

    const summonMatch = effectText.match(/使你召喚(\d+)個小旅人/);
    if (summonMatch) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        targetSelector: {
          zone: 'extra',
          side: 'self',
          filter: { name: '小旅人' },
          count: parseInt(summonMatch[1], 10),
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const sideKey = isPlayer1 ? 'player1Side' : 'player2Side';

    const side = state.battlefield[sideKey];
    const emptySlots: { row: 'front' | 'back'; column: number }[] = [];

    side.frontLine.forEach((slot, idx) => {
      if (!slot.unit) emptySlots.push({ row: 'front', column: idx + 1 });
    });
    side.backLine.forEach((slot, idx) => {
      if (!slot.unit) emptySlots.push({ row: 'back', column: idx + 1 });
    });

    let newState = state;
    const summonsToPlace = Math.min(count, emptySlots.length);

    for (let i = 0; i < summonsToPlace; i++) {
      const slot = emptySlots[i];
      const newCard: Card = {
        id: 'traveler-' + String(Date.now()) + '-' + String(i),
        definitionId: 'trv_trv0001',
        ownerId: playerId,
        location: 'battlefield',
        position: {
          side: isPlayer1 ? 'player1' : 'player2',
          row: slot.row,
          column: slot.column as 1 | 2 | 3 | 4 | 5,
        },
        orientation: 'upright',
        summonedThisTurn: true,
      };

      const lineKey = slot.row === 'front' ? 'frontLine' : 'backLine';
      const newBattlefield = { ...newState.battlefield };
      const newSide = { ...newBattlefield[sideKey] };
      const newLine = [...newSide[lineKey]];
      newLine[slot.column - 1] = { ...newLine[slot.column - 1], unit: newCard };
      newSide[lineKey] = newLine;
      newBattlefield[sideKey] = newSide;

      newState = { ...newState, battlefield: newBattlefield };
    }

    log.debug(`Summoned ${String(summonsToPlace)} travelers`);
    return newState;
  }
}

/**
 * 搜尋牌庫效果處理器
 */
export class SearchDeckEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('從你牌庫尋找') || text.includes('從牌庫尋找')) && text.includes('加入手牌');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const searchMatch = effectText.match(/從你?牌庫尋找(\d*)張?「([^」]+)」[^加]*加入手牌/);
    if (searchMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'searchDeck',
        targetSelector: {
          zone: 'deck',
          side: 'self',
          filter: { name: searchMatch[2] },
          count: searchMatch[1] ? parseInt(searchMatch[1], 10) : 1,
        },
      });
    }

    const factionSearchMatch = effectText.match(/從你?牌庫尋找(\d*)張?([^\s]+)(單位|卡)[^加]*加入手牌/);
    if (factionSearchMatch && !searchMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'searchDeck',
        targetSelector: {
          zone: 'deck',
          side: 'self',
          filter: { faction: factionSearchMatch[2] },
          count: factionSearchMatch[1] ? parseInt(factionSearchMatch[1], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'searchDeck') return state;

    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const cardName = selector.filter?.name;
    const faction = selector.filter?.faction;
    
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    const deck = [...player.deck];
    const hand = [...player.hand];
    let found = 0;

    for (let i = deck.length - 1; i >= 0 && found < count; i--) {
      const card = deck[i];
      if (cardName && card.definitionId.includes(cardName.toLowerCase())) {
        hand.push({ ...deck.splice(i, 1)[0], location: 'hand' as const });
        found++;
      } else if (faction && card.definitionId.includes(faction.toLowerCase())) {
        hand.push({ ...deck.splice(i, 1)[0], location: 'hand' as const });
        found++;
      }
    }

    log.debug(`Found ${String(found)} cards matching criteria`);

    return {
      ...state,
      [playerKey]: {
        ...player,
        deck,
        hand,
      },
    };
  }
}

/**
 * 攻擊力加成效果處理器
 */
export class AttackBuffEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('+') && text.includes('攻擊力')) || text === 'attackBuff';
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使你/敵方場上所有(單位/XXX)+N攻擊力」
    const allBuffMatch = effectText.match(/使(你|敵方)場上所有([^+]+)\+(\d+)攻擊力/);
    if (allBuffMatch) {
      // 「單位」表示所有單位，不是特定名稱的過濾
      const targetText = allBuffMatch[2].trim();
      const isGenericUnit = targetText === '單位' || targetText === '';
      
      actions.push({
        type: 'buff',
        sourceCardId: '',
        value: parseInt(allBuffMatch[3], 10),
        targetSelector: {
          zone: 'battlefield',
          side: allBuffMatch[1] === '敵方' ? 'enemy' : 'self',
          // 如果是「單位」則不過濾，其他則視為種族/名稱過濾
          filter: isGenericUnit ? undefined : { name: targetText },
        },
      });
    }

    const selfBuffMatch = effectText.match(/被賦予\+(\d+)攻擊力/);
    if (selfBuffMatch) {
      actions.push({
        type: 'buff',
        sourceCardId: '',
        value: parseInt(selfBuffMatch[1], 10),
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
          count: 1,
        },
      });
    }

    const attackBuffMatch = effectText.match(/進攻時\+(\d+)攻擊力/);
    if (attackBuffMatch) {
      actions.push({
        type: 'buff',
        sourceCardId: '',
        value: parseInt(attackBuffMatch[1], 10),
        customHandler: 'attackTimeBuff',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const buffValue = typeof action.value === 'number' ? action.value : 0;
    if (buffValue === 0) return state;

    const selector = action.targetSelector;
    if (!selector) {
      log.debug(`No target selector, skipping`);
      return state;
    }

    const isEnemy = selector.side === 'enemy';
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    const targetIsPlayer1 = isEnemy ? !activeIsPlayer1 : activeIsPlayer1;
    const sideKey = targetIsPlayer1 ? 'player1Side' : 'player2Side';

    let newState = state;
    let buffed = 0;

    const buffLine = (lineKey: 'frontLine' | 'backLine'): void => {
      const side = newState.battlefield[sideKey];
      const line = [...side[lineKey]];
      let changed = false;

      for (let i = 0; i < line.length; i++) {
        const unit = line[i].unit;
        if (unit) {
          line[i] = {
            ...line[i],
            unit: {
              ...unit,
              atkModifier: (unit.atkModifier ?? 0) + buffValue,
            },
          };
          changed = true;
          buffed++;
        }
      }

      if (changed) {
        newState = {
          ...newState,
          battlefield: {
            ...newState.battlefield,
            [sideKey]: {
              ...newState.battlefield[sideKey],
              [lineKey]: line,
            },
          },
        };
      }
    };

    buffLine('frontLine');
    buffLine('backLine');

    log.debug(`Buffed ${String(buffed)} units with +${String(buffValue)} ATK`);
    return newState;
  }
}

/**
 * 額外打出效果處理器
 */
export class ExtraPlayEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被額外打出') || text === 'extraPlay';
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const extraPlayMatch = effectText.match(/使你手牌(\d*)張?([^被]+)?被額外打出/);
    if (extraPlayMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'extraPlay',
        targetSelector: {
          zone: 'hand',
          side: 'self',
          count: extraPlayMatch[1] ? parseInt(extraPlayMatch[1], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'extraPlay') return state;

    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const sideKey = isPlayer1 ? 'player1Side' : 'player2Side';
    
    const player = state[playerKey];
    const hand = [...player.hand];

    const side = state.battlefield[sideKey];
    const emptySlots: { row: 'front' | 'back'; column: number }[] = [];

    side.frontLine.forEach((slot, idx) => {
      if (!slot.unit) emptySlots.push({ row: 'front', column: idx + 1 });
    });
    side.backLine.forEach((slot, idx) => {
      if (!slot.unit) emptySlots.push({ row: 'back', column: idx + 1 });
    });

    let newState = state;
    const toPlay = Math.min(count, hand.length, emptySlots.length);

    for (let i = 0; i < toPlay; i++) {
      const card = hand.shift();
      if (!card) break;

      const slot = emptySlots[i];
      const newCard: Card = {
        ...card,
        location: 'battlefield',
        position: {
          side: isPlayer1 ? 'player1' : 'player2',
          row: slot.row,
          column: slot.column as 1 | 2 | 3 | 4 | 5,
        },
        orientation: 'upright',
        summonedThisTurn: true,
      };

      const lineKey = slot.row === 'front' ? 'frontLine' : 'backLine';
      const newBattlefield = { ...newState.battlefield };
      const newSide = { ...newBattlefield[sideKey] };
      const newLine = [...newSide[lineKey]];
      newLine[slot.column - 1] = { ...newLine[slot.column - 1], unit: newCard };
      newSide[lineKey] = newLine;
      newBattlefield[sideKey] = newSide;

      newState = {
        ...newState,
        battlefield: newBattlefield,
        [playerKey]: {
          ...newState[playerKey],
          hand,
        },
      };
    }

    log.debug(`Extra played ${String(toPlay)} cards from hand`);
    return newState;
  }
}

/**
 * 棄牌效果處理器
 */
export class DiscardEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('棄') && text.includes('手牌');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const discardMatch = effectText.match(/棄掉?(\d*)張?手牌/);
    if (discardMatch) {
      actions.push({
        type: 'discard',
        sourceCardId: '',
        value: discardMatch[1] ? parseInt(discardMatch[1], 10) : 1,
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const count = typeof action.value === 'number' ? action.value : 1;
    
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const targetKey = isPlayer1 ? 'player2' : 'player1';

    const player = state[targetKey];
    const hand = [...player.hand];
    const graveyard = [...player.graveyard];

    const toDiscard = Math.min(count, hand.length);
    for (let i = 0; i < toDiscard; i++) {
      const card = hand.pop();
      if (card) {
        graveyard.push({ ...card, location: 'graveyard' as const });
      }
    }

    log.debug(`Discarded ${String(toDiscard)} cards from hand`);

    return {
      ...state,
      [targetKey]: {
        ...player,
        hand,
        graveyard,
      },
    };
  }
}

// ============================================================
// 匯入其他處理器模組
// ============================================================

// 關鍵字處理器
export * from './KeywordHandlers';

// 資源處理器
export * from './ResourceHandlers';

// 牌庫處理器
export * from './DeckHandlers';

// 戰場處理器
export * from './BattlefieldHandlers';

// 戰鬥處理器
export * from './CombatHandlers';

// 特殊處理器
export * from './SpecialHandlers';

// 法術效果處理器
export * from './SpellHandlers';

// 合體效果處理器
export * from './MergeHandlers';

// 搭乘效果處理器
export * from './BoardingHandlers';

// 亡魂效果處理器
export * from './SoulHandlers';

// ============================================================
// 匯出所有基礎處理器
// ============================================================

export const effectHandlers = {
  DestroyEffectHandler,
  ReturnToHandEffectHandler,
  TapEffectHandler,
  SummonTravelerEffectHandler,
  SearchDeckEffectHandler,
  AttackBuffEffectHandler,
  ExtraPlayEffectHandler,
  DiscardEffectHandler,
};
